import { supabase } from '../lib/supabase';
import { logNegotiationAction } from '../lib/negotiationLogger';
import {
    Negociacao, LemitTipo, LemitPessoaData, LemitEmpresaData,
    LemitTelefone, LemitEmail, LemitEndereco, LemitConsulta, LemitSocio,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────
// Serviço Lemit — wrapper da Edge Function `consultar-lemit` + helpers de
// histórico, log de uso e importação seletiva para a negociação.
// ─────────────────────────────────────────────────────────────────────────────

/** Critério de busca aceito pela API Lemit. */
export type LemitCriterio = 'cpf' | 'cnpj';

export interface ConsultarLemitParams {
    criterio: LemitCriterio;
    valor: string;
    negociacaoId?: number;
    segmento?: string;
    forceRefresh?: boolean;
}

export interface LemitConsultaResult {
    data: LemitPessoaData | LemitEmpresaData;
    raw: any;
    tipo: LemitTipo;
    source: 'api' | 'cache';
    cached_at: string;
    cached_by?: string | null;
    id: number | null;
    documento_resolvido?: string;
}

/** Mensagens de erro amigáveis (mesmo padrão de Step4Credito.handleConsultarSerasa). */
async function extractError(res: any, result: any): Promise<string> {
    let msg: string = res.error?.message || 'Erro na consulta Lemit';
    if (result?.error) {
        msg = typeof result.error === 'string' ? result.error : JSON.stringify(result.error);
    } else if (res.error) {
        try {
            const body = await (res.error as any).context?.json?.();
            if (body?.error && typeof body.error === 'string') msg = body.error;
        } catch { /* corpo ilegível, mantém msg */ }
    }
    return msg;
}

/**
 * Consulta a Lemit por CPF, CNPJ, nome ou e-mail via Edge Function. Lança Error com
 * mensagem amigável.
 *
 * ⚠️ NÃO passe `segmento`: a edge function EM PRODUÇÃO repassa o campo à API da Lemit,
 * que responde **404 "Not found"** para segmentos não cadastrados na conta — mesmo com
 * um documento que existe. (O fonte no repo já não repassa, mas nunca foi redeployado;
 * produção também tem busca por nome/email que o repo não tem, então não dá para
 * simplesmente subir o repo por cima.) Todas as 1.776 consultas do banco têm segmento nulo.
 */
export async function consultarLemit(params: ConsultarLemitParams): Promise<LemitConsultaResult> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sessão expirada. Faça login novamente.');

    const valor = (params.criterio === 'cpf' || params.criterio === 'cnpj')
        ? params.valor.replace(/\D/g, '')
        : params.valor.trim();

    const res = await supabase.functions.invoke('consultar-lemit', {
        body: {
            action: 'consulta',
            criterio: params.criterio,
            valor,
            negociacao_id: params.negociacaoId ?? null,
            segmento: params.segmento ?? null,
            force_refresh: !!params.forceRefresh,
        },
    });

    const result = res.data;
    if (res.error || result?.error) {
        throw new Error(await extractError(res, result));
    }
    return result as LemitConsultaResult;
}

/**
 * Detecta um erro de LIMITE da conta Lemit (teto diário de consultas ou rate-limit)
 * a partir da mensagem que a Edge Function repassa ("… - Lemit [429]: limite …").
 *
 * Distinguir isso de "não encontrado" é essencial: um limite atingido NÃO é culpa do
 * documento — não deve marcar a linha como 'falhou' (senão CNPJs válidos viram lixo
 * permanente) e deve PARAR o lote (as próximas consultas também vão falhar até o
 * limite renovar). Ver enriquecerEmpresaRow / rodarLote.
 */
export function isLemitLimitError(msg?: string | null): boolean {
    const s = String(msg || '').toLowerCase();
    if (!s) return false;
    return (
        /limite\s+di[áa]ri/.test(s) ||               // "limite diário"
        /consultas?\s+di[áa]ri/.test(s) ||           // "consultas diárias"
        /limite\s+de\s+consultas/.test(s) ||         // friendlyError do 429
        /limite.*excedid|excedid.*limite/.test(s) || // "limite excedido"
        /\bquota\b|\bcota\b/.test(s) ||
        /too many requests|rate.?limit|limit exceeded/.test(s) ||
        /\b429\b/.test(s)
    );
}

/** Saldo e consumo da conta Lemit (endpoint /saldo). */
export async function getLemitSaldo(): Promise<any> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sessão expirada. Faça login novamente.');

    const res = await supabase.functions.invoke('consultar-lemit', { body: { action: 'saldo' } });
    const result = res.data;
    if (res.error || result?.error) {
        throw new Error(await extractError(res, result));
    }
    return result?.data ?? null;
}

/** Histórico de consultas de um documento específico (badge "consultado por X há Y"). */
export async function getLemitHistory(documento: string): Promise<LemitConsulta[]> {
    const cleanDoc = documento.replace(/\D/g, '');
    const { data } = await supabase
        .from('lemit_consultas')
        .select('id, documento, tipo, segmento, consultado_por, consultado_por_nome, negociacao_id, custo_estimado, source, created_at')
        .eq('documento', cleanDoc)
        .order('created_at', { ascending: false })
        .limit(10);
    return (data as LemitConsulta[]) || [];
}

export interface LemitLogFilters {
    from?: string;   // ISO date
    to?: string;     // ISO date
    userId?: string;
    limit?: number;
}

export interface LemitLogRow extends LemitConsulta {
    negociacoes?: { titulo?: string; cliente_nome?: string } | null;
}

/** Log completo de uso da API (Central de Custos). Inclui título do lead vinculado. */
export async function getLemitLog(filters: LemitLogFilters = {}): Promise<LemitLogRow[]> {
    const sel = 'id, documento, tipo, segmento, consultado_por, consultado_por_nome, negociacao_id, custo_estimado, source, created_at, negociacoes(titulo, cliente_nome)';
    const build = () => {
        let q = supabase.from('lemit_consultas').select(sel).order('created_at', { ascending: false });
        if (filters.from) q = q.gte('created_at', filters.from);
        if (filters.to) q = q.lte('created_at', filters.to);
        if (filters.userId) q = q.eq('consultado_por', filters.userId);
        return q;
    };

    // Sem limite explícito: pagina com .range() até esgotar (o PostgREST corta em
    // 1000/resposta, o que truncava o histórico e escondia consultas antigas).
    if (filters.limit == null) {
        const PAGE = 1000;
        const acc: LemitLogRow[] = [];
        for (let from = 0; ; from += PAGE) {
            const { data } = await build().range(from, from + PAGE - 1);
            const rows = (data as unknown as LemitLogRow[]) || [];
            acc.push(...rows);
            if (rows.length < PAGE) break;
        }
        return acc;
    }

    const { data } = await build().limit(filters.limit);
    return (data as unknown as LemitLogRow[]) || [];
}

export interface LemitUsageStats {
    count: number;
    custoTotal: number;
    custoConhecido: boolean; // false se nenhum registro tem custo configurado
}

/** Estatísticas de uso (contagem + custo) num intervalo ISO (`toISO` p/ o filtro de datas custom). */
export async function getLemitUsageStats(fromISO?: string, userId?: string, toISO?: string): Promise<LemitUsageStats> {
    let query = supabase
        .from('lemit_consultas')
        .select('custo_estimado', { count: 'exact' })
        .eq('source', 'api'); // só consultas que efetivamente bateram na API geram custo
    if (fromISO) query = query.gte('created_at', fromISO);
    if (toISO) query = query.lte('created_at', toISO);
    if (userId) query = query.eq('consultado_por', userId);

    const { data, count } = await query;
    const rows = data || [];
    let custoTotal = 0;
    let custoConhecido = false;
    rows.forEach((r: any) => {
        if (r.custo_estimado != null) { custoTotal += Number(r.custo_estimado); custoConhecido = true; }
    });
    return { count: count ?? rows.length, custoTotal, custoConhecido };
}

/** Carrega o resultado_raw de uma consulta (modal "ver JSON"). */
export async function getLemitConsultaRaw(id: number): Promise<any> {
    const { data } = await supabase.from('lemit_consultas').select('resultado_raw, resultado').eq('id', id).single();
    return data?.resultado_raw ?? data?.resultado ?? null;
}

/**
 * Última consulta vinculada a uma negociação (por negociacao_id), independente do
 * documento. Necessária para leads legados que têm consulta Lemit mas ficaram sem
 * `cpf_cnpj` preenchido — sem isso o painel não acha nada para mostrar.
 */
export async function getLatestLemitConsultaByNegociacao(negociacaoId: number): Promise<LemitConsulta | null> {
    const { data } = await supabase
        .from('lemit_consultas')
        .select('*')
        .eq('negociacao_id', negociacaoId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
    return (data as LemitConsulta) || null;
}

export interface RepresentantePendente {
    negociacao_id: number;
    cliente_nome: string;
    nome_representante_legal: string | null;
    cpf_representante: string;   // dígitos
}

/**
 * Negociações que TÊM representante legal definido mas cujo CPF nunca foi consultado
 * na Lemit — ou seja, sabemos quem é o dono (veio de graça no quadro societário da
 * empresa) mas não temos os dados pessoais dele (telefone/e-mail/renda), que exigem
 * uma consulta paga de CPF.
 *
 * Faz o diff em memória (busca as duas listas paginadas) porque `NOT EXISTS` não é
 * expressável via PostgREST sem uma RPC.
 */
export async function listarRepresentantesPendentes(): Promise<RepresentantePendente[]> {
    const PAGE = 1000;

    // 1) Negociações com CPF de representante
    const comRep: RepresentantePendente[] = [];
    for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
            .from('negociacoes')
            .select('id, cliente_nome, nome_representante_legal, cpf_representante')
            .not('cpf_representante', 'is', null)
            .neq('cpf_representante', '')
            .range(from, from + PAGE - 1);
        if (error) throw error;
        const rows = data || [];
        rows.forEach((n: any) => {
            const cpf = String(n.cpf_representante || '').replace(/\D/g, '');
            if (cpf.length === 11) {
                comRep.push({
                    negociacao_id: n.id,
                    cliente_nome: n.cliente_nome,
                    nome_representante_legal: n.nome_representante_legal ?? null,
                    cpf_representante: cpf,
                });
            }
        });
        if (rows.length < PAGE) break;
    }
    if (comRep.length === 0) return [];

    // 2) CPFs já consultados (tipo pessoa)
    const consultados = new Set<string>();
    for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
            .from('lemit_consultas')
            .select('documento')
            .eq('tipo', 'pessoa')
            .range(from, from + PAGE - 1);
        if (error) throw error;
        const rows = data || [];
        rows.forEach((r: any) => consultados.add(String(r.documento || '').replace(/\D/g, '')));
        if (rows.length < PAGE) break;
    }

    // 3) Diff — dedup por CPF (o mesmo dono pode estar em várias negociações)
    const vistos = new Set<string>();
    return comRep.filter((r) => {
        if (consultados.has(r.cpf_representante)) return false;
        if (vistos.has(r.cpf_representante)) return false;
        vistos.add(r.cpf_representante);
        return true;
    });
}

/** Grava o representante legal (sócio escolhido) na negociação e registra no log. */
export async function setRepresentanteLegal(
    negociacaoId: number,
    socio: { nome?: string; cpf?: string; data_nascimento?: string },
    userId?: string,
): Promise<void> {
    const updates: Record<string, any> = {
        nome_representante_legal: socio.nome || null,
        cpf_representante: (socio.cpf || '').replace(/\D/g, '') || null,
    };
    if (socio.data_nascimento) updates.data_nascimento_representante = socio.data_nascimento;
    const { error } = await supabase.from('negociacoes').update(updates).eq('id', negociacaoId);
    if (error) throw error;
    await logNegotiationAction(negociacaoId, userId, 'definiu o representante legal', {
        nome: socio.nome, cpf: updates.cpf_representante,
    });
}

/** Última consulta completa (resultado + raw) de um documento — exibe dados já obtidos sem custo. */
export async function getLatestLemitConsulta(documento: string, tipo: LemitTipo): Promise<LemitConsulta | null> {
    const cleanDoc = documento.replace(/\D/g, '');
    const { data } = await supabase
        .from('lemit_consultas')
        .select('*')
        .eq('documento', cleanDoc)
        .eq('tipo', tipo)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
    return (data as LemitConsulta) || null;
}

/**
 * Conjunto de leads já enriquecidos pela Lemit — casa por `negociacao_id` OU por
 * `documento` (dígitos do cpf_cnpj), porque um enriquecimento feito na Central Lemit
 * pode não ter `negociacao_id`. Usado para o selo "enriquecido" no Kanban.
 * Retorna dois Sets: ids de negociação e documentos (dígitos) com consulta salva.
 */
export async function getEnrichedKeys(
  negIds: number[],
  documentos: string[],
): Promise<{ negIds: Set<number>; docs: Set<string> }> {
  const outNeg = new Set<number>();
  const outDoc = new Set<string>();
  const ids = Array.from(new Set(negIds.filter((n) => n != null)));
  const docs = Array.from(new Set(documentos.map((d) => (d || '').replace(/\D/g, '')).filter((d) => d.length === 11 || d.length === 14)));

  // Chunks em paralelo: com alguns milhares de leads isso eram ~20 round-trips em
  // série, e o Kanban chamava esta função antes de pintar o board.
  const runIn = async (col: 'negociacao_id' | 'documento', values: (string | number)[]) => {
    const chunks: (string | number)[][] = [];
    for (let i = 0; i < values.length; i += 300) chunks.push(values.slice(i, i + 300));
    const respostas = await Promise.all(chunks.map(chunk =>
      supabase.from('lemit_consultas').select('negociacao_id, documento').in(col, chunk)
    ));
    respostas.forEach(({ data }) => {
      (data || []).forEach((r: any) => {
        if (r.negociacao_id != null) outNeg.add(Number(r.negociacao_id));
        if (r.documento) outDoc.add(String(r.documento).replace(/\D/g, ''));
      });
    });
  };

  if (ids.length) await runIn('negociacao_id', ids);
  if (docs.length) await runIn('documento', docs);
  return { negIds: outNeg, docs: outDoc };
}

// ── Helpers de seleção do "melhor" contato ────────────────────────────────────

const byRanking = (a?: { ranking?: number }, b?: { ranking?: number }) =>
    (a?.ranking ?? 999) - (b?.ranking ?? 999);

export function bestPhone(...lists: (LemitTelefone[] | undefined)[]): LemitTelefone | null {
    const all = lists.flatMap((l) => l || []).filter((p) => p?.numero);
    if (all.length === 0) return null;
    // Prioriza WhatsApp; depois ranking
    const withWa = all.filter((p) => p.whatsapp);
    return (withWa.length ? withWa : all).slice().sort(byRanking)[0] || null;
}

export function bestEmail(emails?: LemitEmail[]): LemitEmail | null {
    const all = (emails || []).filter((e) => e?.email);
    return all.length ? all.slice().sort(byRanking)[0] : null;
}

export function bestEndereco(enderecos?: LemitEndereco[]): LemitEndereco | null {
    const all = (enderecos || []).filter(Boolean);
    return all.length ? all.slice().sort(byRanking)[0] : null;
}

export function phoneDigits(p?: LemitTelefone | null): string {
    if (!p) return '';
    return `${p.ddd ?? ''}${(p.numero ?? '').replace(/\D/g, '')}`.replace(/\D/g, '');
}

/** Converte data ISO da Lemit (1970-10-01T00:00:00-03:00) para YYYY-MM-DD. */
export function isoToDate(iso?: string): string {
    if (!iso) return '';
    const m = iso.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : '';
}

// ── Quadro societário: enriquecimento em lote ─────────────────────────────────

/** Um sócio do quadro societário depois de tentar buscar o dossiê dele na Lemit. */
export interface SocioEnriquecido {
    /** Dígitos do documento (11 = CPF/pessoa, 14 = CNPJ/empresa; '' quando a Lemit não trouxe). */
    cpf: string;
    nome: string;
    participacao?: number | null;
    tipo: LemitTipo;
    data: LemitPessoaData | LemitEmpresaData | null;
    status: 'ok' | 'sem_documento' | 'nao_encontrado' | 'erro';
    /** 'cache' = veio de consulta já salva (sem custo); 'api' = bateu na Lemit. */
    source: 'cache' | 'api' | null;
    consultadoEm?: string | null;
    melhorTelefone?: string;
    melhorEmail?: string;
    erro?: string;
}

const soDigitos = (v?: string | null) => String(v || '').replace(/\D/g, '');

/** "(62) 99999-9999" a partir do telefone da Lemit. */
export function formatTelefoneLemit(t?: LemitTelefone | null): string {
    if (!t?.numero) return '';
    const n = soDigitos(t.numero);
    const num = n.length >= 8 ? `${n.slice(0, n.length - 4)}-${n.slice(-4)}` : n;
    return `(${t.ddd ?? '--'}) ${num}`;
}

/**
 * Última consulta salva de cada documento, numa só ida ao banco. Serve para (a)
 * estimar o custo do lote antes de confirmar e (b) reaproveitar o que já foi pago
 * — a Edge Function só dá cache de 24h, aqui vale qualquer consulta anterior.
 */
export async function getConsultasSalvasPorDoc(documentos: string[]): Promise<Map<string, LemitConsulta>> {
    const docs = Array.from(new Set(
        documentos.map(soDigitos).filter((d) => d.length === 11 || d.length === 14),
    ));
    const out = new Map<string, LemitConsulta>();
    if (docs.length === 0) return out;

    const chunks: string[][] = [];
    for (let i = 0; i < docs.length; i += 200) chunks.push(docs.slice(i, i + 200));
    const respostas = await Promise.all(chunks.map((chunk) =>
        supabase
            .from('lemit_consultas')
            .select('id, documento, tipo, resultado, consultado_por_nome, created_at')
            .in('documento', chunk)
            .order('created_at', { ascending: false }),
    ));
    respostas.forEach(({ data }) => {
        (data || []).forEach((r: any) => {
            const key = soDigitos(r.documento);
            // ordenado desc: a primeira ocorrência de cada documento é a mais recente
            if (r.resultado && !out.has(key)) out.set(key, r as LemitConsulta);
        });
    });
    return out;
}

function resumirContato(data: LemitPessoaData | LemitEmpresaData): Pick<SocioEnriquecido, 'melhorTelefone' | 'melhorEmail'> {
    return {
        melhorTelefone: formatTelefoneLemit(bestPhone(data.celulares, data.fixos)) || undefined,
        melhorEmail: bestEmail(data.emails)?.email || undefined,
    };
}

/**
 * Busca o dossiê de um sócio. Usa a consulta salva quando houver (`salvo`) — sem
 * custo — e só bate na API quando não há nada guardado ou o chamador pediu para
 * reconsultar.
 *
 * ⚠️ Erro de LIMITE da conta é **propagado** (não vira status 'erro'): o lote
 * precisa parar, porque as próximas consultas também falhariam.
 */
export async function enriquecerSocio(
    socio: LemitSocio,
    opts: { salvo?: LemitConsulta | null; negociacaoId?: number; forceRefresh?: boolean } = {},
): Promise<SocioEnriquecido> {
    const doc = soDigitos(socio.cpf);
    const base = {
        cpf: doc,
        nome: socio.nome || '',
        participacao: socio.participacao ?? null,
        tipo: (doc.length === 14 ? 'empresa' : 'pessoa') as LemitTipo,
    };

    if (doc.length !== 11 && doc.length !== 14) {
        return { ...base, data: null, status: 'sem_documento', source: null };
    }

    if (opts.salvo?.resultado && !opts.forceRefresh) {
        return {
            ...base,
            tipo: opts.salvo.tipo || base.tipo,
            data: opts.salvo.resultado,
            status: 'ok',
            source: 'cache',
            consultadoEm: opts.salvo.created_at,
            ...resumirContato(opts.salvo.resultado),
        };
    }

    try {
        const r = await consultarLemit({
            criterio: base.tipo === 'empresa' ? 'cnpj' : 'cpf',
            valor: doc,
            negociacaoId: opts.negociacaoId,
            forceRefresh: opts.forceRefresh,
        });
        return {
            ...base,
            nome: base.nome || (r.tipo === 'empresa' ? (r.data as LemitEmpresaData).razao_social : (r.data as LemitPessoaData).nome) || '',
            tipo: r.tipo,
            data: r.data,
            status: 'ok',
            source: r.source,
            consultadoEm: r.cached_at,
            ...resumirContato(r.data),
        };
    } catch (e: any) {
        const msg = e?.message || 'Erro na consulta Lemit';
        if (isLemitLimitError(msg)) throw e;
        const naoEncontrado = /nao encontrado|não encontrado|\[404\]/i.test(msg);
        return {
            ...base,
            data: null,
            status: naoEncontrado ? 'nao_encontrado' : 'erro',
            source: null,
            erro: msg,
        };
    }
}

// ── Endereço do titular (Termo de Adesão) ─────────────────────────────────────

/** Colapsa espaços duplicados que a Lemit retorna em logradouros ("R  BEZERRA"). */
const cleanText = (s?: string | null): string => String(s || '').replace(/\s+/g, ' ').trim();

/** Normaliza o número da Lemit ("S", "SN" = sem número). */
function cleanNumero(n?: string | null): string {
    const v = cleanText(n);
    return /^S\/?N?$/i.test(v) ? 'S/N' : v;
}

/** Formata CEP de 8 dígitos como 75903-070. */
export function formatCep(cep?: string | null): string {
    const d = String(cep || '').replace(/\D/g, '');
    return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : cleanText(cep);
}

/** Endereço cadastral do titular já normalizado para o payload do termo. */
export interface EnderecoTitular {
    rua: string;
    numero: string;
    complemento: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
    // quadra/lote só existem quando o endereço escolhido veio do cadastro de uma UC
    quadra?: string;
    lote?: string;
    fonte: 'cache' | 'api' | 'uc';
}

function extractEnderecos(resultado: LemitPessoaData | LemitEmpresaData, tipo: LemitTipo): LemitEndereco[] {
    if (tipo === 'empresa') {
        const end = (resultado as LemitEmpresaData).endereco;
        return end ? [end] : [];
    }
    return ((resultado as LemitPessoaData).enderecos || []).filter(Boolean).slice().sort(byRanking);
}

function normalizeEndereco(endereco: LemitEndereco, fonte: 'cache' | 'api'): EnderecoTitular | null {
    const rua = cleanText(endereco.logradouro) || cleanText(endereco.endereco);
    if (!rua) return null;
    return {
        rua,
        numero: cleanNumero(endereco.numero),
        complemento: cleanText(endereco.complemento),
        bairro: cleanText(endereco.bairro),
        cidade: cleanText(endereco.cidade),
        estado: cleanText(endereco.uf),
        cep: formatCep(endereco.cep),
        fonte,
    };
}

/** Chave de comparação p/ deduplicar endereços (Lemit repete e UC pode coincidir com Lemit). */
export function enderecoKey(e: Pick<EnderecoTitular, 'rua' | 'numero' | 'cep' | 'cidade'>): string {
    return [e.rua, e.numero, e.cep, e.cidade]
        .map(v => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, ''))
        .join('|');
}

/**
 * Todos os endereços cadastrais do CPF/CNPJ via Lemit, ordenados por ranking e
 * deduplicados — o modal do termo oferece a lista para o usuário escolher qual
 * sai como endereço do contratante. Reaproveita a última consulta salva em
 * lemit_consultas (sem custo); só chama a API quando não há endereço salvo
 * (a Edge Function ainda aplica cache de 24h). `forceRefresh` ignora tudo que
 * está salvo e força uma consulta nova (paga).
 */
export async function getEnderecosTitularLemit(documento: string, negociacaoId?: number, forceRefresh = false): Promise<EnderecoTitular[]> {
    const doc = (documento || '').replace(/\D/g, '');
    if (doc.length !== 11 && doc.length !== 14) return [];
    const tipo: LemitTipo = doc.length === 14 ? 'empresa' : 'pessoa';

    let enderecos: LemitEndereco[] = [];
    let fonte: 'cache' | 'api' = 'cache';

    if (!forceRefresh) {
        const cached = await getLatestLemitConsulta(doc, tipo);
        if (cached?.resultado) enderecos = extractEnderecos(cached.resultado, tipo);
    }

    if (enderecos.length === 0) {
        // sem `segmento`: 'termo_adesao' era repassado à Lemit pela função em produção
        // e derrubava a busca de endereço do titular com 404 (ver consultarLemit).
        const res = await consultarLemit({
            criterio: tipo === 'empresa' ? 'cnpj' : 'cpf',
            valor: doc,
            negociacaoId,
            forceRefresh,
        });
        enderecos = extractEnderecos(res.data, tipo);
        fonte = 'api';
    }

    const seen = new Set<string>();
    const result: EnderecoTitular[] = [];
    for (const end of enderecos) {
        const normalizado = normalizeEndereco(end, fonte);
        if (!normalizado) continue;
        const key = enderecoKey(normalizado);
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(normalizado);
    }
    return result;
}

/**
 * Melhor endereço cadastral do CPF/CNPJ via Lemit (menor ranking) — usado quando
 * o termo é gerado sem passar pelo modal de seleção. Retorna null se a Lemit não
 * tiver endereço para o documento.
 */
export async function getEnderecoTitularLemit(documento: string, negociacaoId?: number, forceRefresh = false): Promise<EnderecoTitular | null> {
    const enderecos = await getEnderecosTitularLemit(documento, negociacaoId, forceRefresh);
    return enderecos[0] ?? null;
}

// ── Importação seletiva (revisar e importar) ──────────────────────────────────

/** Coluna da negociação que recebe o valor importado. */
export type ImportTargetKey =
    | 'cliente_nome' | 'whatsapp' | 'email' | 'cpf_cnpj'
    | 'data_nascimento_cliente' | 'cidade' | 'bairro';

export interface ImportField {
    key: ImportTargetKey;
    label: string;
    crmAtual: string;       // valor atual no CRM (vazio = "—")
    lemitValor: string;     // valor sugerido pela Lemit (vazio = não importável)
    aplicarDefault: boolean; // ligado por padrão só quando o CRM está vazio e há valor
}

function field(key: ImportTargetKey, label: string, crmAtual: any, lemitValor: string): ImportField {
    const crm = crmAtual == null ? '' : String(crmAtual).trim();
    const val = (lemitValor || '').trim();
    return { key, label, crmAtual: crm, lemitValor: val, aplicarDefault: !!val && !crm };
}

/** Monta a lista de campos importáveis comparando o lead atual com o retorno da Lemit. */
export function buildImportDiff(
    negotiation: Negociacao,
    lemitData: LemitPessoaData | LemitEmpresaData,
    tipo: LemitTipo,
): ImportField[] {
    if (tipo === 'pessoa') {
        const d = lemitData as LemitPessoaData;
        const tel = bestPhone(d.celulares, d.fixos);
        const mail = bestEmail(d.emails);
        const end = bestEndereco(d.enderecos);
        return [
            field('cliente_nome', 'Nome', negotiation.cliente_nome, d.nome || ''),
            field('whatsapp', 'WhatsApp / Telefone', negotiation.whatsapp, phoneDigits(tel)),
            field('email', 'E-mail', negotiation.email, mail?.email || ''),
            field('data_nascimento_cliente', 'Data de nascimento', negotiation.data_nascimento_cliente, isoToDate(d.data_nascimento)),
            field('cidade', 'Cidade', negotiation.cidade, end?.cidade || ''),
            field('bairro', 'Bairro', negotiation.bairro, end?.bairro || ''),
        ].filter((f) => f.lemitValor !== '');
    }

    const d = lemitData as LemitEmpresaData;
    const tel = bestPhone(d.celulares, d.fixos);
    const mail = bestEmail(d.emails);
    const end = d.endereco || null;
    return [
        field('cliente_nome', 'Razão social', negotiation.cliente_nome, d.razao_social || d.nome_fantasia || ''),
        field('whatsapp', 'WhatsApp / Telefone', negotiation.whatsapp, phoneDigits(tel)),
        field('email', 'E-mail', negotiation.email, mail?.email || ''),
        field('cidade', 'Cidade', negotiation.cidade, end?.cidade || ''),
        field('bairro', 'Bairro', negotiation.bairro, end?.bairro || ''),
    ].filter((f) => f.lemitValor !== '');
}

/** Grava no lead apenas os campos marcados e registra a ação. */
export async function applyImport(
    negociacaoId: number,
    selecionados: ImportField[],
    userId: string | undefined,
): Promise<void> {
    const updates: Record<string, any> = {};
    selecionados.forEach((f) => { if (f.lemitValor) updates[f.key] = f.lemitValor; });
    if (Object.keys(updates).length === 0) return;

    const { error } = await supabase.from('negociacoes').update(updates).eq('id', negociacaoId);
    if (error) throw error;

    await logNegotiationAction(negociacaoId, userId, 'importou dados da Lemit', {
        campos: Object.keys(updates),
        valores: updates,
    });
}
